import i18next from "i18next";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { SINGLE_MODIFIERS_GROUP_ID } from "#/constants/modifiers";

import { getRegularModifierGroups, isSwiperModifierGroup, pickDefaultSwiperModifier, resolveExclusiveModifierAmount, toSelectedGroup } from "#/utils/customRenderModifiers";
import { getGroupsPaidTotal } from "#/utils/freeModifiers";
import { getLocaleName } from "#/utils/i18next";
import { collectMarkingCodes } from "#/utils/markingCodes";
import { getModifierPriceForDishSize } from "#/utils/syncModifierWithSize";
import type { IGroupModifierSelected, IGroupModifiers, IGroupSelect, IPopupVisible, IProduct, ISelectModifiers, ISizes } from "../types/iCardProduct";
import PricePrerender from "../utils/pricePrerender";
import { useHotmenuStore } from "./hotmenu";
import { useMainStore } from "./main";
import { useThemeStore } from "./theme";

export const useCardStore = defineStore("cardLib", () => {
	const getInitialStateCard = () => ({
		openCard: null as IProduct | null,
		currentModifier: null as ISelectModifiers | null,
		groupModifiers: [] as IGroupModifiers[],
		groupModifiersSelected: [] as IGroupModifierSelected[],
		groupCurrentOpen: null as IGroupSelect | null,
		isModifiedAmount: false as boolean,
		selectedSize: {} as ISizes,
		productSizes: [] as any[],
		addedProducts: [] as IProduct[],
		modalCount: 1 as number,
		isSectionModifier: false as boolean,
		isAnimationFly: true as boolean,
		textErrorRequaired: "" as string,
		prevLast: {} as IGroupModifiers,
	});

	const hotmenu = useHotmenuStore();
	const themeStore = useThemeStore();
	const mainStore = useMainStore();

	const stateCard = ref(getInitialStateCard());
	const reset = () => (stateCard.value = getInitialStateCard());
	const isOpenWindow = ref(false);
	const isUpSale = ref(false);
	const isAddToCard = ref(false);
	const isCombo = ref(false);
	const dataUpSale = ref();
	const dataCombo = computed(() => {
		if (!stateCard.value.openCard?.has_combo && !stateCard.value.openCard?.id) return [];
		return (
			Array.from(hotmenu.combos.values())?.filter((combo) =>
				combo.groups.filter((group) => group.is_main).some((group) => group.products.some((product) => product.id === stateCard.value.openCard?.id)),
			) || []
		);
	});
	const productCards = ref<any[]>([]);
	const openProduct = ref<IPopupVisible>({ id: null, visible: false });

	const is_startPage = ref(localStorage.getItem("kiosk_disable_lock_screen") !== "true");

	//Общий счетчик модификаторов группы который натыкали
	const recountGroupTotalAmount = computed(() => {
		const groupIndex = stateCard.value.groupModifiersSelected.findIndex((g) => g.group === stateCard.value.groupCurrentOpen!.id);
		return summModifierAmount(groupIndex);
	});

	const summModifierAmount = (groupIndex: number) => {
		if (groupIndex === -1) return 0;

		const modifiersCount = stateCard.value.groupModifiersSelected[groupIndex].modifiers || [];

		return modifiersCount.reduce((groupSum, modifier) => {
			return groupSum + (modifier.amount || 0);
		}, 0);
	};

	//Общий счетчик max_amount для группы
	const groupTotalAmount = computed(() => {
		const currentOpen = stateCard.value.groupCurrentOpen;
		const openCard = stateCard.value.openCard;
		if (!currentOpen || !openCard) return 0;

		if (currentOpen.id === SINGLE_MODIFIERS_GROUP_ID) {
			const productModifiers = openCard.modifiers ?? [];
			if (productModifiers.length > 0) {
				return productModifiers.reduce((groupSum, modifier) => groupSum + (modifier.max_amount ?? 0), 0);
			}
		}

		const fromState = stateCard.value.groupModifiers.find((group) => group.id === currentOpen.id);
		return fromState?.max_amount ?? 0;
	});

	//Общая цена
	const totalPrice_libCard = computed(() => {
		const addedCalculatedPrice = (stateCard.value.addedProducts ?? []).reduce((acc, group) => acc + group.price, 0);

		if (stateCard.value.openCard) {
			const cartProductsCopy = stateCard.value.openCard;
			const basePrice = PricePrerender(cartProductsCopy) || 0;
			const amount = cartProductsCopy.amount || 1;

			const selectedSize = stateCard.value.selectedSize;
			const pricedGroups = stateCard.value.groupModifiersSelected.map((group) => ({
				...group,
				modifiers: group.modifiers.map((modifier) => ({
					...modifier,
					price: getModifierPriceForDishSize(modifier, selectedSize),
				})),
			}));
			const resultGroups = getGroupsPaidTotal(pricedGroups, stateCard.value.groupModifiers);
			return (resultGroups + basePrice) * amount + addedCalculatedPrice;
		}
		return stateCard.value.addedProducts?.length ? addedCalculatedPrice : 0;
	});

	const isLastGroup = computed(() => {
		const current = stateCard.value.groupCurrentOpen;
		if (!current) return false;
		const regular = getRegularModifierGroups(stateCard.value.groupModifiers);
		const currentIndex = regular.findIndex((g) => g.id === current.id);
		if (currentIndex === -1) return false;
		return currentIndex === regular.length - 1;
	});

	//Проверка одной группы по индексу
	const validateGroup = (groupIndex: number): { valid: boolean; errorText?: string } => {
		const group = stateCard.value.groupModifiers[groupIndex];
		const selGroup = stateCard.value.groupModifiersSelected[groupIndex];
		if (!group) return { valid: false };

		const groupName = getLocaleName(group, i18next.language);

		if (group.min_amount > 0 && summModifierAmount(groupIndex) < group.min_amount) {
			return {
				valid: false,
				// @tolgee-key { key: 'card.error.min-amount', ns: 'translation' }
				errorText: i18next.t(`card.error.min-amount`, { name: groupName, amount: group.min_amount }),
			};
		}

		if (selGroup) {
			for (const mod of group.group_modifiers) {
				if (mod.min_amount > 0) {
					const selMod = selGroup.modifiers.find((m) => m.id === mod.id);
					if (!selMod || selMod.amount < mod.min_amount) {
						return {
							valid: false,
							// @tolgee-key { key: 'card.error.required-modifiers', ns: 'translation' }
							errorText: i18next.t(`card.error.required-modifiers`, { name: groupName }),
						};
					}
				}
			}
		}

		return { valid: true };
	};

	//Проверка есть ли обязательные модификаторы в карточках и больше ли min_amount у выбранных
	const checkedRequiredBtn = computed(() => {
		const groups = stateCard.value.groupModifiers;

		for (let i = 0; i < groups.length; i++) {
			const result = validateGroup(i);
			if (!result.valid) {
				stateCard.value.textErrorRequaired = result.errorText!;
				return { haveRequaire: true, group: groups[i] };
			}
		}
		return { haveRequaire: false, group: groups[0] };
	});

	//Выставляет дефолтные модификаторы при получении данных
	const defaultAmountModifiers = () => {
		stateCard.value.groupModifiers.forEach((group, index) => {
			stateCard.value.groupCurrentOpen = { ...group, index: index };
			group.group_modifiers.forEach((modifier) => {
				if (modifier.default_amount > 0) {
					modifier.amount = modifier.default_amount;
					modifierSelect(modifier, true);
				}
			});

			if (!isSwiperModifierGroup(group)) return;
			const selected = stateCard.value.groupModifiersSelected[index];
			const hasSelection = (selected?.modifiers ?? []).some((modifier) => (modifier.amount ?? 0) > 0);
			if (hasSelection) return;

			const fallback = pickDefaultSwiperModifier(group);
			if (!fallback) return;
			fallback.amount = resolveExclusiveModifierAmount(fallback);
			modifierSelect(fallback, true);
		});
	};

	const setAddedProducts = (product: IProduct) => {
		stateCard.value.addedProducts.push(product);
	};

	const setCurrentCard = (card: any) => {
		stateCard.value.openCard = card;
	};

	const setProduct = (products: any) => {
		productCards.value = [...products];
	};

	//Открытие и закрытие шторки с модификаторами
	const toggleNotRequire = (group: IGroupModifiers, _index?: number, isFollow?: boolean) => {
		const actualIndex = stateCard.value.groupModifiers.findIndex((item) => item.id === group.id);
		if (actualIndex === -1) return;

		if (!isFollow) {
			const isOpenGroup = group.id !== stateCard.value.groupCurrentOpen?.id;
			stateCard.value.groupCurrentOpen = { ...group, index: actualIndex };
			stateCard.value.isSectionModifier = isOpenGroup ? true : !stateCard.value.isSectionModifier;
		} else {
			const groups = stateCard.value.groupModifiers;
			let nextIndex = actualIndex + 1;
			while (nextIndex < groups.length && isSwiperModifierGroup(groups[nextIndex])) nextIndex++;

			if (nextIndex < groups.length) {
				const nextGroup = groups[nextIndex];
				stateCard.value.groupCurrentOpen = { ...nextGroup, index: nextIndex };
				stateCard.value.isSectionModifier = true;
			}
		}
	};
	const getRemainingSlotsExcludingModifier = (groupIndex: number, modifierId: string): number => {
		const groupMax = groupTotalAmount.value ?? 0;
		if (groupMax <= 0) return Infinity;

		const selectedInGroup = groupIndex >= 0 ? summModifierAmount(groupIndex) : 0;
		const existing = groupIndex >= 0 ? stateCard.value.groupModifiersSelected[groupIndex].modifiers.find((m) => m.id === modifierId) : null;
		const existingAmount = existing?.amount ?? 0;

		return Math.max(0, groupMax - selectedInGroup + existingAmount);
	};

	const resolveDirectAddAmount = (modifier: ISelectModifiers, groupIndex: number, requestedAmount?: number): number => {
		const requested = requestedAmount ?? modifier.amount ?? 1;
		const remaining = getRemainingSlotsExcludingModifier(groupIndex, modifier.id);
		let amount = remaining === Infinity ? requested : Math.min(requested, remaining);

		if (modifier.max_amount > 0) {
			amount = Math.min(amount, modifier.max_amount);
		}

		return Math.max(0, amount);
	};

	//Минус у модального окна модификаторов
	const minusAmount = () => {
		if (stateCard.value.modalCount > stateCard.value.currentModifier!.min_amount && stateCard.value.modalCount >= 2) {
			stateCard.value.modalCount -= 1;
		}
	};

	//Плюс у модального окна модификаторов
	const plusAmount = () => {
		if (!stateCard.value.groupCurrentOpen || !stateCard.value.currentModifier) return;

		const modifier = stateCard.value.currentModifier;
		const groupIndex = stateCard.value.groupModifiersSelected.findIndex((g) => g.group === stateCard.value.groupCurrentOpen!.id);
		const groupMax = groupTotalAmount.value ?? 0;

		if (groupMax > 0) {
			const selectedInGroup = groupIndex >= 0 ? summModifierAmount(groupIndex) : 0;
			const existing = groupIndex >= 0 ? stateCard.value.groupModifiersSelected[groupIndex].modifiers.find((m) => m.id === modifier.id) : null;
			const existingAmount = existing?.amount ?? 0;
			const totalAfterAdd = selectedInGroup - existingAmount + stateCard.value.modalCount + 1;

			if (totalAfterAdd > groupMax) return;
		}

		if (modifier.max_amount > 0 && stateCard.value.modalCount >= modifier.max_amount) return;

		stateCard.value.modalCount += 1;
	};

	//Проверка открытия окна модального (только если в группе осталось больше 1 слота)
	const _checkOpenModal = (isDefault: boolean, modifier: ISelectModifiers, groupIndex: number) => {
		if (isDefault || stateCard.value.isModifiedAmount) return false;
		if (modifier.max_amount === 1) return false;

		const remaining = getRemainingSlotsExcludingModifier(groupIndex, modifier.id);
		if (remaining <= 1) return false;

		// NOTE: намеренно сохранённый «баг»: .toUpperCase() даёт "ДОБАВКИ2000", что никогда не равно
		// SINGLE_MODIFIERS_GROUP_ID ("Добавки2000") → первая ветка всегда false, вторая всегда true.
		// Поведение классической карточки на это завязано; фикс — отдельной задачей (см. vault: dobavki2000-magic-group).
		const supplementsId = stateCard.value.groupCurrentOpen?.id.toUpperCase();

		if (supplementsId === SINGLE_MODIFIERS_GROUP_ID && modifier.max_amount > 1) {
			return true;
		}

		if (supplementsId !== SINGLE_MODIFIERS_GROUP_ID && (groupTotalAmount.value ?? 0) > 1) {
			return true;
		}

		return false;
	};

	const isMarkingGated = (modifier: ISelectModifiers): boolean =>
		!!modifier.is_marked && !!themeStore.data?.retail_mode && typeof mainStore.modifierMarkRequester === "function";

	const requestMarks = async (modifier: ISelectModifiers, count: number): Promise<string[] | null> => {
		const codes: string[] = [];
		for (let i = 0; i < count; i++) {
			const code = await mainStore.modifierMarkRequester?.(modifier, [
				...codes,
				...stateCard.value.groupModifiersSelected.flatMap((group) => collectMarkingCodes(group)),
			]);
			if (code == null) return null;
			codes.push(code);
		}
		return codes;
	};

	//Функции при нажатии на модификатор
	const modifierSelect = async (modifier: ISelectModifiers, isDefault: boolean = false) => {
		stateCard.value.currentModifier = modifier;

		const groupIndex = stateCard.value.groupModifiersSelected.findIndex((g) => g.group === stateCard.value.groupCurrentOpen!.id);

		const existing = groupIndex >= 0 ? stateCard.value.groupModifiersSelected[groupIndex].modifiers.find((m) => m.id === modifier.id) : null;

		const remaining = getRemainingSlotsExcludingModifier(groupIndex, modifier.id);

		if (existing) {
			stateCard.value.modalCount = existing.amount;
		} else {
			const initial = modifier.amount ?? 1;
			stateCard.value.modalCount = remaining === Infinity ? initial : Math.min(initial, Math.max(1, remaining));
		}

		await _addRemoveGroupModifier(groupIndex, modifier, isDefault);
	};

	const _addRemoveGroupModifier = async (index: number, modifier: ISelectModifiers, isDefault: boolean) => {
		const selectedGroup = stateCard.value.groupModifiersSelected;
		const productId = stateCard.value.openCard?.id;

		if (index === -1) {
			//Группы нет в выбранных, добавляется
			const targetGroup = stateCard.value.groupCurrentOpen;
			if (_checkOpenModal(isDefault, modifier, index)) {
				stateCard.value.isModifiedAmount = true;
				stateCard.value.isAnimationFly = false;
			} else {
				const amount = resolveDirectAddAmount(modifier, index);
				if (amount <= 0) {
					stateCard.value.isAnimationFly = false;
					return;
				}

				let modifierToStore: ISelectModifiers = modifier;
				if (!isDefault && isMarkingGated(modifier)) {
					const codes = await requestMarks(modifier, amount);
					if (stateCard.value.openCard?.id !== productId) return;
					if (!codes) {
						stateCard.value.isAnimationFly = false;
						return;
					}
					modifierToStore = { ...modifier, marking_codes: codes };
				}

				selectedGroup[targetGroup!.index] = toSelectedGroup(targetGroup!, [{ ...modifierToStore, amount }]);

				stateCard.value.isAnimationFly = true;
			}
		} else {
			//Группа в выбранных
			const indexCurrentModifier = stateCard.value.groupModifiers[index].group_modifiers.findIndex((item) => item.id === modifier.id);
			const indexSelectModifier = selectedGroup[index].modifiers.findIndex((m) => m.id === modifier.id);
			if (indexSelectModifier === -1) {
				//Модификатор на который нажали не выбран, добавляется
				if (1 === groupTotalAmount.value) {
					selectedGroup[index].modifiers = [];
				} else if (recountGroupTotalAmount.value >= groupTotalAmount.value!) {
					stateCard.value.isAnimationFly = false;
					return;
				}

				if (_checkOpenModal(isDefault, modifier, index)) {
					stateCard.value.isModifiedAmount = true;
					stateCard.value.isAnimationFly = false;
				} else {
					const amount = resolveDirectAddAmount(modifier, index);
					if (amount <= 0) {
						stateCard.value.isAnimationFly = false;
						return;
					}

					let modifierToStore: ISelectModifiers = modifier;
					if (!isDefault && isMarkingGated(modifier)) {
						const codes = await requestMarks(modifier, amount);
						if (stateCard.value.openCard?.id !== productId) return;
						if (!codes) {
							stateCard.value.isAnimationFly = false;
							return;
						}
						modifierToStore = { ...modifier, marking_codes: codes };
					}

					selectedGroup[index].modifiers.push({
						...modifierToStore,
						amount,
					});
					selectedGroup[index].modifier_count = selectedGroup[index].modifiers.reduce((acc, item) => (acc += item.amount), 0);
					if (selectedGroup[index].free_quantity === undefined) {
						selectedGroup[index].free_quantity = stateCard.value.groupCurrentOpen?.free_quantity ?? stateCard.value.groupModifiers[index]?.free_quantity ?? null;
					}
					stateCard.value.isAnimationFly = true;
				}
			} else {
				//Модификатор на который нажали выбран, удаляется
				selectedGroup[index].modifiers = selectedGroup[index].modifiers.filter((m) => m.id !== modifier.id);
				selectedGroup[index].modifier_count = selectedGroup[index].modifiers.reduce((acc, item) => (acc += item.amount), 0);
				stateCard.value.groupModifiers[index].group_modifiers[indexCurrentModifier].amount = 1;
				stateCard.value.groupModifiers[index].group_modifiers[indexCurrentModifier].default_amount = 0;
				stateCard.value.isAnimationFly = false;
			}
		}
		stateCard.value.groupModifiersSelected = selectedGroup;
	};

	return {
		stateCard,
		totalPrice_libCard,
		recountGroupTotalAmount,
		groupTotalAmount,
		isOpenWindow,
		isUpSale,
		checkedRequiredBtn,
		dataUpSale,
		productCards,
		isAddToCard,
		dataCombo,
		isCombo,
		openProduct,
		is_startPage,
		isLastGroup,

		setProduct,
		summModifierAmount,
		setAddedProducts,
		setCurrentCard,
		minusAmount,
		plusAmount,
		toggleNotRequire,
		modifierSelect,
		defaultAmountModifiers,
		validateGroup,
		reset,
	};
});
